import {
  ColumnDef,
  ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingFn,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { com } from '@eidu/form'
import { NavigateFunction } from 'react-router-dom'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Menu,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { ArrowDownward, ArrowUpward, DragIndicator, MoreVert, PanToolOutlined } from '@mui/icons-material'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityWithLabel from '../../domain/entity/EntityWithLabel'
import DraftFieldValue, { draftFieldValueToFieldValue, fieldValueToString } from '../../domain/entity/DraftFieldValue'
import hasFieldError from '../../domain/entity/hasFieldError'
import { logException } from '../../util/Logging'
import EntityRepository from '../../io/EntityRepository'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import usePermittedActions from '../../io/usePermittedActions'
import useEntityCount from '../../io/useEntityCount'
import getRenderFieldCell, { CellContent } from '../misc/getRenderFieldCell'
import getRenderInvitationCell from '../misc/getRenderInvitationCell'
import { useSnackBar } from '../feedback/SnackBarProvider'
import EntityFormInput from '../input/EntityFormInput'
import useDebounced from '../../ui/debounce/useDebounced'
import { isManualOrderField, isOrderField } from '../../util/entityType/orderField'
import { getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import { useAssistantContext } from '../assistant/AssistantContext'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import Field = com.eidu.sharedlib.entity.field.Field
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import phoneNumberLoginIdentifierKey = com.eidu.sharedlib.entity.phoneNumberLoginIdentifierKey
import emailLoginIdentifierKey = com.eidu.sharedlib.entity.emailLoginIdentifierKey
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import invitationStatusKey = com.eidu.sharedlib.entity.invitationStatusKey

const getIdMap = (entities: readonly EntityWithLabelAndRelated[]): ReadonlyMap<EntityId, EntityWithLabel> =>
  new EqualityHashMap(
    entities.flatMap((entity) => [
      [entity.entity.id, entity] as [EntityId, EntityWithLabel],
      ...entity.referencedEntities.map((ref) => [ref.entity.id, ref] as [EntityId, EntityWithLabel]),
    ])
  )

const naturalCompare = (a: string | undefined | null, b: string | undefined | null): number => {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

const naturalSortingFn: SortingFn<TableRowData> = (rowA, rowB, columnId) => {
  const a = rowA.original.sortValues[columnId] ?? (rowA.getValue(columnId) as string | undefined)
  const b = rowB.original.sortValues[columnId] ?? (rowB.getValue(columnId) as string | undefined)
  return naturalCompare(a, b)
}

const parseOrderValue = (value: string | undefined | null): number | null => {
  if (value == null) return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseFloat(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const orderSortingFn: SortingFn<TableRowData> = (rowA, rowB) => {
  const a = rowA.original.orderValue
  const b = rowB.original.orderValue
  if (a == null && b == null) {
    return rowA.original.entityId.localeCompare(rowB.original.entityId, undefined, { sensitivity: 'base' })
  }
  if (a == null) return 1
  if (b == null) return -1
  if (a !== b) return a - b
  return rowA.original.entityId.localeCompare(rowB.original.entityId, undefined, { sensitivity: 'base' })
}

const DROP_TARGET_HEIGHT = 12
const MIN_ORDER_VALUE = 0
const MAX_ORDER_VALUE = 1_000_000_000_000_000
const GAP_ID_PREFIX = 'row-gap:'

const getGapId = (index: number) => `${GAP_ID_PREFIX}${index}`

type TableRowData = {
  entityId: string
  rowNumber: number
  orderValue: number | null
  entity: EntityWithLabelAndRelated
  fieldValues: Record<string, string | undefined>
  sortValues: Record<string, string | undefined>
  phoneNumberLoginIdentifier?: string
  emailLoginIdentifier?: string
  invitationStatus?: string
}

type BulkEditMenuProps = {
  field: Field
  anchorEl: HTMLElement | null
  onClose: () => void
  selectedEntityIds: string[]
  entityRepository: EntityRepository
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  onEditComplete: () => void
}

const BulkEditMenu = ({
  field,
  anchorEl,
  onClose,
  selectedEntityIds,
  entityRepository,
  entityTypes,
  onEditComplete,
}: BulkEditMenuProps) => {
  const { showSnackBar } = useSnackBar()

  const referenceableEntityTypeIds = Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? [])
  const firstReferenceTypeId = referenceableEntityTypeIds[0]
  const shouldLoadEntities = referenceableEntityTypeIds.length === 1 && firstReferenceTypeId !== undefined

  const { entities: referenceableEntities } = usePaginatedEntitiesOfType({
    typeId: firstReferenceTypeId ?? referenceableEntityTypeIds[0] ?? entityTypes.keys().next().value,
    allTypes: entityTypes,
  })

  const referenceableEntitiesByTypeId: ReadonlyMap<
    EntityTypeId,
    ReadonlyMap<EntityId, EntityWithLabelAndRelated>
  > = shouldLoadEntities && firstReferenceTypeId
    ? new EqualityHashMap([
        [firstReferenceTypeId, new EqualityHashMap(referenceableEntities.map((e) => [e.entity.id, e]))],
      ])
    : new Map()

  const defaultFieldValue = { fieldId: field.id, type: field.type, value: '' }
  const [fieldValue, setFieldValue] = useState<DraftFieldValue>(defaultFieldValue)

  const [isEditing, setIsEditing] = useState(false)

  const hasError = field && hasFieldError(field, fieldValue, referenceableEntitiesByTypeId)

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          overflow: 'visible',
        },
      }}
    >
      <Box sx={{ p: 2, minWidth: 300, display: 'flex', flexDirection: 'column' }}>
        <Stack spacing={2}>
          <Typography variant="h6">Field ID</Typography>
          <Typography color="gray">{field.id.toString()}</Typography>
          <Typography variant="h6">Edit selected items</Typography>
          <EntityFormInput
            key={`column-form-field-${field.id.toString()}`}
            field={field}
            fieldValue={fieldValue}
            entityTypes={entityTypes}
            updateField={setFieldValue}
          />
          {selectedEntityIds.length === 0 && (
            <Typography variant="body2" color="warning.main" sx={{ fontStyle: 'italic' }}>
              Please select one or more rows for batch editing
            </Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" sx={{ height: '40px' }}>
            <Button
              variant="contained"
              disabled={!selectedEntityIds.length || hasError || isEditing}
              onClick={async () => {
                setIsEditing(true)
                try {
                  const fieldValuesById = new Map([[field.id, draftFieldValueToFieldValue(fieldValue)]])
                  await entityRepository.modifyEntities(
                    new Map(
                      selectedEntityIds.map((id) => [
                        entityIdFromString(id),
                        {
                          valuesToUpdateByFieldId: fieldValuesById,
                          linksToUpdate: new Map(),
                          externalValuesToUpdate: new Map(),
                        },
                      ])
                    )
                  )
                  showSnackBar(`Updated ${selectedEntityIds.length} items`, 'success')
                  onEditComplete()
                  onClose()
                } catch (e) {
                  logException(e)
                  showSnackBar(`Failed to update ${selectedEntityIds.length} items`, 'error')
                }
                setIsEditing(false)
              }}
            >
              {isEditing ? <CircularProgress size={24} /> : `Apply (${selectedEntityIds.length})`}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Menu>
  )
}

type RowDropTargetProps = {
  id: string
  top: number
  isDragging: boolean
}

const RowDropTarget = ({ id, top, isDragging }: RowDropTargetProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    disabled: !isDragging,
  })
  const offset = Math.max(0, top - DROP_TARGET_HEIGHT / 2)

  return (
    <TableRow
      ref={setNodeRef}
      component="div"
      role="row"
      sx={{
        position: 'absolute',
        top: `${offset}px`,
        left: 0,
        right: 0,
        height: `${DROP_TARGET_HEIGHT}px`,
        display: 'flex',
        alignItems: 'center',
        pointerEvents: isDragging ? 'auto' : 'none',
        opacity: isDragging ? 1 : 0,
        transition: 'opacity 0.1s ease',
        zIndex: isOver ? 3 : 2,
        border: 0,
      }}
    >
      <TableCell
        component="div"
        role="cell"
        sx={{
          flex: 1,
          border: 'none',
          p: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            height: 2,
            width: '100%',
            bgcolor: isOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            opacity: isOver ? 1 : 0.6,
          }}
        />
      </TableCell>
    </TableRow>
  )
}

type DraggableRowProps = {
  row: Row<TableRowData>
  virtualRow: VirtualItem
  measureElement: (element: HTMLElement | null) => void
  navigate: NavigateFunction
  entityTypeId: EntityTypeId
  manualOrderingEnabled: boolean
  dragOverlayEnabled: boolean
}

const DraggableRow = ({
  row,
  virtualRow,
  measureElement,
  navigate,
  entityTypeId,
  manualOrderingEnabled,
  dragOverlayEnabled,
}: DraggableRowProps) => {
  const draggable = useDraggable({ id: row.id, disabled: !manualOrderingEnabled })
  const dragListeners = draggable.listeners ?? {}
  const isDragging = draggable.isDragging
  const hideWhileDragging = isDragging && dragOverlayEnabled

  const setRowRef = (node: HTMLElement | null) => {
    draggable.setNodeRef(node)
    measureElement(node)
  }

  const cells = row.getVisibleCells()
  const translateX = draggable.transform?.x ?? 0
  const translateY = draggable.transform?.y ?? 0
  const transform = hideWhileDragging
    ? undefined
    : draggable.transform && (translateX !== 0 || translateY !== 0)
      ? `translate3d(${translateX}px, ${translateY}px, 0)`
      : undefined

  return (
    <TableRow
      key={row.id}
      component="div"
      role="row"
      data-index={virtualRow.index}
      ref={setRowRef}
      onClick={() => {
        if (isDragging) return
        const id = entityIdFromString(row.id)
        if (id) navigate(`/entities/${entityTypeId.toString()}/${id.toString()}`)
      }}
      sx={{
        cursor: 'pointer',
        position: 'absolute',
        top: virtualRow.start,
        left: 0,
        width: '100%',
        display: 'flex',
        opacity: hideWhileDragging ? 0.2 : isDragging ? 0.6 : 1,
        zIndex: isDragging ? 2 : 0,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
      style={{ transform }}
    >
      {cells.map((cell, index) => {
        const isRowCell = manualOrderingEnabled && cell.column.id === 'row'
        return (
          <TableCell
            key={cell.id}
            component="div"
            role="cell"
            sx={{
              width: cell.column.getSize(),
              flexGrow: index === cells.length - 1 ? 1 : 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {isRowCell ? (
              <IconButton
                size="small"
                ref={draggable.setActivatorNodeRef}
                aria-label="Drag to reorder"
                sx={{ cursor: manualOrderingEnabled ? 'grab' : 'default' }}
                {...draggable.attributes}
                {...dragListeners}
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => {
                  event.stopPropagation()
                  dragListeners.onPointerDown?.(event)
                }}
              >
                <DragIndicator fontSize="small" />
              </IconButton>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        )
      })}
    </TableRow>
  )
}

type DragOverlayRowProps = {
  row: Row<TableRowData>
  manualOrderingEnabled: boolean
  width?: number
}

const DragOverlayRow = ({ row, manualOrderingEnabled, width }: DragOverlayRowProps) => {
  const cells = row.getVisibleCells()

  return (
    <Table
      component="div"
      role="table"
      size="small"
      sx={{
        tableLayout: 'fixed',
        minWidth: 'max-content',
        pointerEvents: 'none',
        display: 'block',
      }}
      style={{ width: width ? `${width}px` : undefined }}
    >
      <TableBody component="div" role="rowgroup" sx={{ display: 'block' }}>
        <TableRow
          component="div"
          role="row"
          sx={{
            display: 'flex',
            width: '100%',
            backgroundColor: 'transparent',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {cells.map((cell, index) => {
            const isRowCell = manualOrderingEnabled && cell.column.id === 'row'
            return (
              <TableCell
                key={cell.id}
                component="div"
                role="cell"
                sx={{
                  width: cell.column.getSize(),
                  flexGrow: index === cells.length - 1 ? 1 : 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isRowCell ? (
                  <IconButton size="small" sx={{ cursor: 'grab' }} aria-hidden>
                    <DragIndicator fontSize="small" />
                  </IconButton>
                ) : (
                  flexRender(cell.column.columnDef.cell, cell.getContext())
                )}
              </TableCell>
            )
          })}
        </TableRow>
      </TableBody>
    </Table>
  )
}

type TanStackDataGridProps = {
  navigate: NavigateFunction
  entityRepository: EntityRepository
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  entityType: EntityType
  pageSize: number
}

const TanStackDataGrid = ({ navigate, entityRepository, entityTypes, entityType, pageSize }: TanStackDataGridProps) => {
  const { showSnackBar } = useSnackBar()
  const requested = useRef([new PermittedGlobalAction(GlobalAction.ManageAuthentication)])
  const [permissions] = usePermittedActions(requested.current)

  const hasManageAuthenticationPermission = () =>
    permissions?.some((permission) => permission.equals(new PermittedGlobalAction(GlobalAction.ManageAuthentication)))

  const showAuthenticationInfo = entityType.kind === EntityTypeKind.User && hasManageAuthenticationPermission()

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebounced(searchQuery)

  const totalCount = useEntityCount({ typeId: entityType.id, searchQuery: debouncedSearchQuery })

  const { entities, loadMore, reload, hasMore, loading } = usePaginatedEntitiesOfType({
    typeId: entityType.id,
    searchQuery: debouncedSearchQuery,
    allTypes: entityTypes,
    pageSize,
  })

  const orderField = useMemo(
    () => entityType.fields.asJsReadonlyArrayView().find((field) => isOrderField(field)),
    [entityType.fields]
  )
  const hasOrderField = !!orderField
  const orderFieldKey = orderField ? `field-${orderField.id.toString()}` : undefined
  const isSearchActive = searchQuery.trim().length > 0 || debouncedSearchQuery.trim().length > 0
  const isFullyLoaded = useMemo(() => {
    if (loading) return false
    if (totalCount !== undefined) return entities.length >= totalCount
    return !hasMore
  }, [entities.length, hasMore, loading, totalCount])

  const entityIdMap = useMemo(() => getIdMap(entities), [entities])

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [sorting, setSorting] = useState<SortingState>([{ id: 'row', desc: false }])
  const [bulkEditMenu, setBulkEditMenu] = useState<{ anchorEl: HTMLElement; field: Field } | null>(null)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const columnVisibility = useMemo(() => ({ row: hasOrderField }), [hasOrderField])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const isRowOrderingActive = useMemo(() => {
    const current = sorting[0]
    if (!current) return true
    return sorting.length === 1 && current.id === 'row' && !current.desc
  }, [sorting])

  const manualOrderingEnabled = hasOrderField && isFullyLoaded && !isSearchActive && isRowOrderingActive

  const setRowSorting = useCallback(() => {
    setSorting((previous) => {
      const current = previous[0]
      if (current?.id === 'row' && !current.desc) return previous
      return [{ id: 'row', desc: false }]
    })
  }, [])

  const handleColumnSort = useCallback((columnId: string) => {
    setSorting((previous) => {
      const current = previous[0]
      if (!current || current.id !== columnId) return [{ id: columnId, desc: false }]
      if (!current.desc) return [{ id: columnId, desc: true }]
      return [{ id: 'row', desc: false }]
    })
  }, [])

  useEffect(() => {
    setRowSelection({})
  }, [entityType.id, searchQuery])

  const tableData: TableRowData[] = useMemo(
    () =>
      entities.map((entity, index) => {
        const fieldValues = Object.fromEntries(
          entityType.fields.asJsReadonlyArrayView().map((field) => [
            `field-${field.id.toString()}`,
            entity.entity.valuesByFieldId
              .asJsReadonlyMapView()
              .get(field.id)
              ?.let((it) => fieldValueToString(it)),
          ])
        )

        const sortValues = Object.fromEntries(
          entityType.fields.asJsReadonlyArrayView().map((field) => {
            const fieldKey = `field-${field.id.toString()}`
            const rawValue = fieldValues[fieldKey]

            if (field.type === FieldType.Reference && rawValue) {
              const refId = entityIdFromStringOrNull(rawValue)
              if (refId) {
                const refEntity = entityIdMap.get(refId)
                return [fieldKey, refEntity?.primaryLabelText ?? rawValue]
              }
            }
            return [fieldKey, rawValue]
          })
        )

        const orderValue = orderFieldKey ? parseOrderValue(fieldValues[orderFieldKey]) : null

        return {
          entityId: entity.entity.id.toString(),
          rowNumber: index + 1,
          orderValue,
          entity,
          fieldValues,
          sortValues,
          ...(showAuthenticationInfo
            ? {
                phoneNumberLoginIdentifier:
                  entity.entity.externalValues.asJsReadonlyMapView().get(phoneNumberLoginIdentifierKey) || '',
                emailLoginIdentifier:
                  entity.entity.externalValues.asJsReadonlyMapView().get(emailLoginIdentifierKey) || '',
                invitationStatus: entity.entity.externalValues.asJsReadonlyMapView().get(invitationStatusKey) || '',
              }
            : {}),
        }
      }),
    [entities, entityType.fields, orderFieldKey, showAuthenticationInfo, entityIdMap]
  )

  const entityIdMapRef = useRef(entityIdMap)
  entityIdMapRef.current = entityIdMap

  const reloadRef = useRef(reload)
  reloadRef.current = reload

  const columns = useMemo<ColumnDef<TableRowData>[]>(() => {
    const fieldColumns: ColumnDef<TableRowData>[] = entityType.fields
      .asJsReadonlyArrayView()
      .filter((field) => !isManualOrderField(field))
      .map((field) => ({
        id: `field-${field.id.toString()}`,
        header: ({ column }) => (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                cursor: 'pointer',
                flex: 1,
              }}
              onClick={() => handleColumnSort(column.id)}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{field.name}</span>
              {{
                asc: <ArrowUpward sx={{ fontSize: 16 }} />,
                desc: <ArrowDownward sx={{ fontSize: 16 }} />,
              }[column.getIsSorted() as string] ?? null}
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setBulkEditMenu({ anchorEl: e.currentTarget as HTMLElement, field })
              }}
              sx={{ padding: 0.5, position: 'absolute', right: 8 }}
              aria-label={`Menu for ${field.name}`}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </>
        ),
        accessorFn: (row) => row.fieldValues[`field-${field.id.toString()}`],
        cell: (info) => {
          const cellContent: CellContent = { value: info.getValue() as string | undefined }
          const renderCell = getRenderFieldCell(field.type, entityIdMapRef.current, entityTypes)
          return renderCell({
            value: cellContent,
            row: { id: info.row.original.entityId },
          } as unknown as GridRenderCellParams<GridValidRowModel, CellContent>)
        },
        sortingFn: naturalSortingFn,
        size: 200,
        minSize: 100,
      }))

    const baseColumns: ColumnDef<TableRowData>[] = [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            inputProps={{ 'aria-label': 'Select all rows' }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
            inputProps={{ 'aria-label': `Select row ${row.original.rowNumber}` }}
          />
        ),
        size: 50,
        enableResizing: false,
        enableSorting: false,
      },
      {
        id: 'row',
        header: () => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              width: '100%',
            }}
            onClick={setRowSorting}
            title="Manual order"
            aria-label="Sort by row"
          >
            <PanToolOutlined fontSize="small" />
            {isRowOrderingActive ? <ArrowUpward sx={{ fontSize: 16 }} /> : null}
          </Box>
        ),
        accessorKey: 'rowNumber',
        cell: () => null,
        sortingFn: orderSortingFn,
        size: 70,
        minSize: 50,
      },
    ]

    const authColumns: ColumnDef<TableRowData>[] = showAuthenticationInfo
      ? [
          {
            id: 'phoneNumberLoginIdentifier',
            header: ({ column }) => (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                onClick={() => handleColumnSort(column.id)}
              >
                <span>Access Phone Number</span>
                {{
                  asc: <ArrowUpward sx={{ fontSize: 16 }} />,
                  desc: <ArrowDownward sx={{ fontSize: 16 }} />,
                }[column.getIsSorted() as string] ?? null}
              </Box>
            ),
            accessorKey: 'phoneNumberLoginIdentifier',
            sortingFn: naturalSortingFn,
            size: 180,
            minSize: 100,
          },
          {
            id: 'emailLoginIdentifier',
            header: ({ column }) => (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                onClick={() => handleColumnSort(column.id)}
              >
                <span>Access Email</span>
                {{
                  asc: <ArrowUpward sx={{ fontSize: 16 }} />,
                  desc: <ArrowDownward sx={{ fontSize: 16 }} />,
                }[column.getIsSorted() as string] ?? null}
              </Box>
            ),
            accessorKey: 'emailLoginIdentifier',
            sortingFn: naturalSortingFn,
            size: 180,
            minSize: 100,
          },
          {
            id: 'invitationStatus',
            header: ({ column }) => (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                onClick={() => handleColumnSort(column.id)}
              >
                <span>Invitation</span>
                {{
                  asc: <ArrowUpward sx={{ fontSize: 16 }} />,
                  desc: <ArrowDownward sx={{ fontSize: 16 }} />,
                }[column.getIsSorted() as string] ?? null}
              </Box>
            ),
            accessorKey: 'invitationStatus',
            sortingFn: naturalSortingFn,
            size: 100,
            minSize: 80,
            cell: (info) => {
              const renderCell = getRenderInvitationCell(() => reloadRef.current())
              return renderCell({
                value: info.getValue() as string,
                row: {
                  id: info.row.original.entityId,
                  phoneNumberLoginIdentifier: info.row.original.phoneNumberLoginIdentifier,
                  emailLoginIdentifier: info.row.original.emailLoginIdentifier,
                },
              } as unknown as GridRenderCellParams<GridValidRowModel, string>)
            },
          },
        ]
      : []

    return [...baseColumns, ...fieldColumns, ...authColumns]
  }, [entityType.fields, entityTypes, handleColumnSort, isRowOrderingActive, setRowSorting, showAuthenticationInfo])

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      rowSelection,
      columnSizing,
      sorting,
      columnVisibility,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      setSorting((previous) => {
        const next = typeof updater === 'function' ? updater(previous) : updater
        if (next.length === 0) return [{ id: 'row', desc: false }]
        return next.map((sort) => (sort.id === 'row' ? { ...sort, desc: false } : sort))
      })
    },
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.entityId,
  })

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 45,
    overscan: 5,
  })

  const { setCurrentView } = useAssistantContext()

  const virtualRows = rowVirtualizer.getVirtualItems()
  const lastVirtualRowIndex = virtualRows[virtualRows.length - 1]?.index ?? -1

  const visibleEntities = useMemo(() => {
    const visibleRows = virtualRows.length > 0 ? virtualRows.map((row) => rows[row.index]).filter(Boolean) : rows
    return visibleRows.slice(0, 20).map((row) => ({
      id: row.original.entityId,
      label: row.original.entity.primaryLabelText.trim() || undefined,
    }))
  }, [rows, virtualRows])

  useEffect(() => {
    setCurrentView({
      kind: 'list',
      typeId: entityType.id.toString(),
      typeName: getTypeDisplayName(entityType.name),
      visibleEntities,
    })
    return () => {
      setCurrentView(undefined)
    }
  }, [entityType.id, entityType.name, setCurrentView, visibleEntities])

  useEffect(() => {
    if (lastVirtualRowIndex >= rows.length - 1 && hasMore && !loading) {
      loadMore()
    }
  }, [hasMore, loading, rows.length, lastVirtualRowIndex, loadMore])

  const selectedEntityIds = Object.keys(rowSelection)
  const isDragging = activeDragId !== null
  const dragOverlayEnabled = manualOrderingEnabled
  const activeDragRow = activeDragId ? rows.find((row) => row.id === activeDragId) : undefined

  const handleDragStart = (event: DragStartEvent) => {
    if (!manualOrderingEnabled) return
    setActiveDragId(event.active.id.toString())
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragId(null)
    if (!manualOrderingEnabled || !orderField) return
    if (!event.over) return
    const overId = event.over.id.toString()
    if (!overId.startsWith(GAP_ID_PREFIX)) return
    const gapIndex = Number.parseInt(overId.slice(GAP_ID_PREFIX.length), 10)
    if (!Number.isFinite(gapIndex)) return

    const activeId = event.active.id.toString()
    const activeIndex = rows.findIndex((row) => row.id === activeId)
    if (activeIndex === -1) return

    const rowsWithoutActive = rows.filter((row) => row.id !== activeId)
    const targetIndex = gapIndex > activeIndex ? gapIndex - 1 : gapIndex
    const clampedIndex = Math.max(0, Math.min(targetIndex, rowsWithoutActive.length))
    const beforeRow = clampedIndex > 0 ? rowsWithoutActive[clampedIndex - 1] : undefined
    const afterRow = clampedIndex < rowsWithoutActive.length ? rowsWithoutActive[clampedIndex] : undefined
    const lowerBound = beforeRow?.original.orderValue ?? MIN_ORDER_VALUE
    const upperBound = afterRow?.original.orderValue ?? MAX_ORDER_VALUE
    const nextOrderValue = Math.floor((lowerBound + upperBound) / 2)

    const entityId = entityIdFromString(activeId)
    if (!entityId) return

    try {
      await entityRepository.modifyEntity(entityId, {
        valuesToUpdateByFieldId: new Map([
          [
            orderField.id,
            draftFieldValueToFieldValue({
              fieldId: orderField.id,
              type: FieldType.Number,
              value: `${nextOrderValue}`,
            }),
          ],
        ]),
        linksToUpdate: new Map(),
        externalValuesToUpdate: new Map(),
      })
      reloadRef.current()
    } catch (error) {
      logException(error)
      showSnackBar('Failed to update order', 'error')
    }
  }

  const handleDragCancel = () => {
    setActiveDragId(null)
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: 'background.paper',
          py: 1,
          px: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <TextField
          label="Search"
          size="small"
          value={searchQuery}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(event.target.value)
          }}
          sx={{ minWidth: 250 }}
        />
        <Typography variant="body2" color="primary">
          {selectedEntityIds.length > 0
            ? `${selectedEntityIds.length} row${selectedEntityIds.length === 1 ? '' : 's'} selected`
            : ''}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {totalCount !== undefined ? `${totalCount} total` : 'Loading count...'}
        </Typography>
      </Box>
      <Box
        ref={tableContainerRef}
        data-testid="entity-table-container"
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Table
            ref={tableRef}
            component="div"
            role="table"
            size="small"
            sx={{ tableLayout: 'fixed', minWidth: 'max-content', display: 'block' }}
          >
            <TableHead
              component="div"
              role="rowgroup"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: 'block',
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  component="div"
                  role="row"
                  sx={{ display: 'flex', backgroundColor: 'background.paper', minWidth: '100%' }}
                >
                  {headerGroup.headers.map((header, index) => (
                    <TableCell
                      key={header.id}
                      component="div"
                      role="columnheader"
                      sx={{
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize ?? header.getSize(),
                        flexGrow: index === headerGroup.headers.length - 1 ? 1 : 0,
                        position: 'relative',
                        backgroundColor: 'inherit',
                        fontWeight: 'bold',
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanResize() && (
                        <Box
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          sx={{
                            position: 'absolute',
                            right: 1,
                            top: '25%',
                            height: '50%',
                            width: 2,
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            borderRadius: 0.5,
                            backgroundColor: header.column.getIsResizing() ? 'primary.main' : 'divider',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                            },
                          }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody
              component="div"
              role="rowgroup"
              sx={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: 'relative',
                display: 'block',
              }}
            >
              {rows.length === 0 && !loading ? (
                <TableRow component="div" role="row" sx={{ display: 'flex', width: '100%' }}>
                  <TableCell component="div" role="cell" sx={{ flex: 1, textAlign: 'center', border: 'none' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No entities found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {manualOrderingEnabled &&
                    virtualRows.map((virtualRow) => (
                      <RowDropTarget
                        key={getGapId(virtualRow.index)}
                        id={getGapId(virtualRow.index)}
                        top={virtualRow.start}
                        isDragging={isDragging}
                      />
                    ))}
                  {manualOrderingEnabled && virtualRows.length > 0 && (
                    <RowDropTarget
                      key={getGapId(virtualRows[virtualRows.length - 1].index + 1)}
                      id={getGapId(virtualRows[virtualRows.length - 1].index + 1)}
                      top={virtualRows[virtualRows.length - 1].end}
                      isDragging={isDragging}
                    />
                  )}
                  {virtualRows.map((virtualRow) => {
                    const row = rows[virtualRow.index]
                    return (
                      <DraggableRow
                        key={row.id}
                        row={row}
                        virtualRow={virtualRow}
                        measureElement={rowVirtualizer.measureElement}
                        navigate={navigate}
                        entityTypeId={entityType.id}
                        manualOrderingEnabled={manualOrderingEnabled}
                        dragOverlayEnabled={dragOverlayEnabled}
                      />
                    )
                  })}
                </>
              )}
            </TableBody>
          </Table>
          {dragOverlayEnabled && (
            <DragOverlay>
              {activeDragRow ? (
                <DragOverlayRow
                  row={activeDragRow}
                  manualOrderingEnabled={manualOrderingEnabled}
                  width={tableRef.current?.scrollWidth || tableRef.current?.offsetWidth}
                />
              ) : null}
            </DragOverlay>
          )}
        </DndContext>
        {loading && (
          <Box
            sx={{
              py: 2,
              position: 'sticky',
              left: 0,
              width: tableContainerRef.current?.clientWidth ?? '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
      </Box>
      {bulkEditMenu && (
        <BulkEditMenu
          field={bulkEditMenu.field}
          anchorEl={bulkEditMenu.anchorEl}
          onClose={() => setBulkEditMenu(null)}
          selectedEntityIds={selectedEntityIds}
          entityRepository={entityRepository}
          entityTypes={entityTypes}
          onEditComplete={() => {
            reload()
            setRowSelection({})
          }}
        />
      )}
    </Box>
  )
}

export default TanStackDataGrid
